import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase.js'
import { apiFetch } from '../lib/api.js'
import { pageBg as podiumBg } from '../lib/images'

function WaitingDots() {
  return (
    <div className="flex gap-1.5 justify-center mt-3">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="w-2.5 h-2.5 bg-white/60 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  )
}

export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const [errorMsg, setErrorMsg] = useState(null)

  useEffect(() => {
    // Supabase renvoie les erreurs OAuth dans les params URL (query ou hash)
    const params = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    const error = params.get('error') || hashParams.get('error')
    const description = params.get('error_description') || hashParams.get('error_description')

    if (error) {
      const msg = description ? decodeURIComponent(description.replace(/\+/g, ' ')) : error
      setErrorMsg(msg)
      setTimeout(() => navigate('/login', { replace: true }), 4000)
      return
    }

    let handled = false

    async function processSession(session) {
      if (handled || !session) return
      handled = true

      // Lire le profil pour vérifier si l'onboarding est fait
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, onboarding_completed')
        .eq('id', session.user.id)
        .single()

      if (!profile || !profile.onboarding_completed) {
        navigate('/onboarding', { replace: true })
        return
      }

      // Traitement du quiz partagé en attente (doit être fait avant la navigation pour éviter
      // la course entre le POST et le chargement de HomePage)
      const raw = localStorage.getItem('kart_pending_share')
      let toastMessage = null
      if (raw) {
        let pending
        try { pending = JSON.parse(raw) } catch { localStorage.removeItem('kart_pending_share') }
        if (pending) {
          localStorage.removeItem('kart_pending_share')
          try {
            await apiFetch('/api/share/add', { method: 'POST', body: JSON.stringify({ deck_id: pending.deckId }) })
            toastMessage = `"${pending.deckTitle}" ajouté à tes quiz partagés !`
          } catch {
            toastMessage = `Impossible d'ajouter le quiz partagé. Réessaie via le lien.`
          }
        }
      }

      // Redirection vers la Home pour tous les rôles
      navigate('/home', { replace: true, state: toastMessage ? { toastMessage } : undefined })
    }

    // onAuthStateChange gère les deux flux : PKCE (?code=) et implicit (#access_token=)
    // SIGNED_IN se déclenche après l'échange du code PKCE, contrairement à getSession()
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' || (event === 'INITIAL_SESSION' && session)) {
          processSession(session)
        }
      }
    )

    // Délai de sécurité : si aucune session après 8s, retour au login
    const timeout = setTimeout(() => {
      if (!handled) {
        handled = true
        navigate('/login', { replace: true })
      }
    }, 8000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [navigate])

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{ backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl px-8 py-6 text-center max-w-sm">
        {errorMsg ? (
          <>
            <p className="text-2xl mb-2">⚠️</p>
            <p className="text-white font-medium text-base mb-1">Échec de la connexion</p>
            <p className="text-white/70 text-sm mb-3">{errorMsg}</p>
            <p className="text-white/50 text-xs">Redirection vers la page de connexion…</p>
          </>
        ) : (
          <>
            <p className="text-white font-medium text-base">Connexion en cours…</p>
            <WaitingDots />
          </>
        )}
      </div>
    </div>
  )
}
